import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BrandLogo } from "@/components/BrandLogo";
import { SiteFooter } from "@/components/SiteFooter";

export function LegalPageLayout({
  title,
  updated,
  children,
}: {
  title: string;
  updated: string;
  children: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" dir={isAr ? "rtl" : "ltr"}>
      <header className="border-b border-white/5 bg-zinc-950/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <Link to="/" aria-label={t("legalLayout.backHome")}>
            <BrandLogo size="md" variant="light" />
          </Link>
          <Link
            to="/"
            className="text-sm text-zinc-400 transition-colors hover:text-white"
          >
            {isAr ? "→" : "←"} {t("legalLayout.backHome")}
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        <div className="mb-10 border-b border-white/5 pb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-300">
            {t("legalLayout.eyebrow")}
          </p>
          <h1 className="mt-3 text-4xl font-bold tracking-tight text-white sm:text-5xl">{title}</h1>
          <p className="mt-3 text-sm text-zinc-500">
            {t("legalLayout.lastUpdated")}: {updated}
          </p>
        </div>

        <article className="prose prose-invert prose-zinc max-w-none space-y-6 text-zinc-300 [&_h2]:mt-10 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-white [&_h3]:mt-6 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-white [&_p]:leading-relaxed [&_p]:text-zinc-400 [&_ul]:list-disc [&_ul]:ps-6 [&_ul]:text-zinc-400 [&_li]:my-1 [&_a]:text-indigo-300 [&_a:hover]:text-indigo-200">
          {children}
        </article>
      </main>

      <SiteFooter />
    </div>
  );
}
